import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getSecurityControls } from '../../redux/securityCheckFamily/securityCheckFamilyAction'
import { useTranslation } from 'react-i18next'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import { useSelector } from 'react-redux'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { formatTitle } from '../../utils/formatTitle'
import { splitFamilyLabel } from '../../utils/familyLabel'
import { formatNormLabel } from '../../utils/normLabels'
import {
  ListPageShell,
  listPageIconBtnStyle,
} from '../../components/common/ListPageShell'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import TableEmptyState from '../../components/common/TableEmptyState'
import TableLoadingState from '../../components/common/TableLoadingState'
import TablePager from '../../components/common/TablePager'
import { useClientPagination } from '../../hooks/useClientPagination'
import { useTableLoadingGate } from '../../hooks/useTableLoadingGate'
import { onClickableTableRow } from '../../utils/tableRowClick'
import MappingFlowStepper from './MappingFlowStepper'
import '../../components/common/ListPageTable.css'
import '../securityFramework/SecurityFramework.css'
import './MappingFlow.css'

function ControlsToMap() {
  const { id } = useParams<{ id: string }>()
  const { SecurityId } = useParams<{ SecurityId: string }>()
  const dispatch: AppDispatch = useAppDispatch()
  const navigate = useNavigate()
  const securityCheckFamilysList = useSelector(
    (state: RootState) => state.securitycheckFamily.SecurityControls
  )
  const loading = useSelector(
    (state: RootState) => state.securitycheckFamily.loading
  )
  const showLoader = useTableLoadingGate(loading, [SecurityId])
  const [search, setSearch] = useState('')
  const { t } = useTranslation()
  const selectedNorm = useSelector(selectSpecifications)[0]?.NormBase || ''

  useEffect(() => {
    dispatch(getSecurityControls(SecurityId || ''))
  }, [dispatch, SecurityId])

  const controls = securityCheckFamilysList || []
  const filteredControls = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return controls
    return controls.filter((control: any) =>
      [control.Label, control.Description, control.Source]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    )
  }, [controls, search])

  const pager = useClientPagination(filteredControls, 10)

  const availableLabels = useMemo(
    () =>
      new Set(
        controls.flatMap((control: any) =>
          (control.RelatedControls || []).flatMap(
            (related: any) => related.ControlIds || []
          )
        )
      ).size,
    [controls]
  )

  const sourceFramework = formatNormLabel(id || '')
  const targetFramework = formatNormLabel(selectedNorm)

  const openControl = (controlId: string) => {
    navigate(`/security-control-details/${controlId}`)
  }

  const openRelated = (controlId: string) => {
    navigate(`/relatedDataMapped/${id}/${controlId}`)
  }

  return (
    <div className="mapping-flow">
      <MappingFlowStepper
        currentStep={3}
        sourceNorm={id}
        familyId={SecurityId}
      />

      <ListPageShell
        eyebrow={t('frameworkMappingPage.controlsEyebrow')}
        title={t('frameworkMappingPage.controlsTitle', {
          source: sourceFramework,
        })}
        subtitle={t('frameworkMappingPage.controlsSubtitle', {
          target: targetFramework,
        })}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('frameworkMappingPage.filterControls')}
        countLabel={t('frameworkMappingPage.controlsCount', {
          count: filteredControls.length,
        })}
        showFiltersChip={false}
      >
        <div className="mapping-flow-shell-body">
          <div className="fw-source-bar mapping-flow-context">
            <div className="fw-stat">
              <div className="fw-stat-label">
                {t('frameworkMappingPage.sourceFramework')}
              </div>
              <div className="fw-stat-value">{sourceFramework}</div>
            </div>
            <div className="fw-stat-divider" aria-hidden />
            <div className="fw-stat">
              <div className="fw-stat-label">
                {t('frameworkMappingPage.targetFramework')}
              </div>
              <div className="fw-stat-value">{targetFramework}</div>
            </div>
            <div className="fw-stat-divider" aria-hidden />
            <div className="fw-stat">
              <div className="fw-stat-label">
                {t('frameworkMappingPage.controlsLoaded')}
              </div>
              <div className="fw-stat-value fw-mono">{controls.length}</div>
            </div>
            <div className="fw-stat-divider" aria-hidden />
            <div className="fw-stat">
              <div className="fw-stat-label">
                {t('frameworkMappingPage.availableLabels')}
              </div>
              <div className="fw-stat-value fw-mono">{availableLabels}</div>
            </div>
            <button
              type="button"
              className="fw-ghost-btn"
              onClick={() => navigate(`/norms/${id}`)}
            >
              {t('frameworkMappingPage.backToFamilies')}
            </button>
          </div>

          <div className="incyia-table-wrap">
            {showLoader ? (
              <TableLoadingState />
            ) : filteredControls.length === 0 ? (
              <TableEmptyState />
            ) : (
              <>
                <table className="incyia-table incyia-table--controls">
                  <thead>
                    <tr>
                      <th>{t('Label')}</th>
                      <th>{t('Description')}</th>
                      <th>{t('Source')}</th>
                      <th style={{ textAlign: 'right' }}>
                        {t('frameworkMappingPage.RelatedControls')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pager.pageItems.map((control: any) => {
                      const controlId = control._id as string
                      const parsed = splitFamilyLabel(control.Label || '')
                      const source = String(control.Source || '')
                        .replace(/\s*\|\s*/g, ' | ')
                        .trim()
                      return (
                        <tr key={controlId} onClick={onClickableTableRow}>
                          <td className="incyia-col-pinned">
                            <button
                              type="button"
                              className="incyia-family-label"
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                textAlign: 'left',
                                width: '100%',
                              }}
                              onClick={() => openControl(controlId)}
                            >
                              <span className="incyia-family-code">
                                {parsed.full ? parsed.code : formatTitle(parsed.code)}
                              </span>
                              {parsed.full ? (
                                <span
                                  className="incyia-family-name"
                                  title={formatTitle(parsed.full)}
                                >
                                  {formatTitle(parsed.full)}
                                </span>
                              ) : null}
                            </button>
                          </td>
                          <td className="incyia-wrap">
                            <ListPageDescription value={control.Description} />
                          </td>
                          <td>
                            <span className="incyia-family-source">
                              {source || '—'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <div className="incyia-row-actions">
                              <button
                                type="button"
                                className="mapping-flow-row-cta"
                                title={t('frameworkMappingPage.RelatedControls')}
                                onClick={() => openRelated(controlId)}
                                style={listPageIconBtnStyle}
                              >
                                <span className="mapping-flow-row-cta-label">
                                  {t('frameworkMappingPage.reviewShort')}
                                </span>
                                <ChevronRightIcon sx={{ fontSize: 16 }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                {pager.showPager ? (
                  <TablePager
                    page={pager.page}
                    pageSize={pager.pageSize}
                    total={pager.total}
                    pageCount={pager.pageCount}
                    from={pager.from}
                    to={pager.to}
                    pageSizeOptions={pager.pageSizeOptions}
                    onPageChange={pager.setPage}
                    onPageSizeChange={pager.setPageSize}
                  />
                ) : null}
              </>
            )}
          </div>
        </div>
      </ListPageShell>
    </div>
  )
}

export default ControlsToMap
